import React, { useState, useEffect } from "react";
import NavigationHeader from "../../components/ui/NavigationHeader";
import MainNavigation from "../../components/ui/MainNavigation";
import BreadcrumbNavigation from "../../components/ui/BreadcrumbNavigation";
import QuickActionPanel from "../../components/ui/QuickActionPanel";
import CustomerTable from "./components/CustomerTable";
import CustomerFilters from "./components/CustomerFilters";
import CustomerModal from "./components/CustomerModal";
import CustomerToolbar from "./components/CustomerToolbar";
import DeleteConfirmationModal from "./components/DeleteConfirmationModal";
import { useDispatch, useSelector } from "react-redux";
import { getClientsService } from "redux/services/clients_service";
import CustomerPagination from "./components/CustomerPagination";
import CustomerDetails from "./components/CustomerDetails";

const CustomerManagement = () => {
  const dispatch = useDispatch();

  const { clientsList, metaClient } = useSelector((state) => state.clients);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [filters, setFilters] = useState({
    status: "",
    location: "",
    dateFrom: "",
    dateTo: "",
  });

  // Modal states
  const [customerModal, setCustomerModal] = useState({
    isOpen: false,
    mode: "view", // 'view', 'edit', 'create'
    customer: null,
  });

  // Modal states
  const [customerInfoModal, setCustomerInfoModal] = useState({
    isOpenInfo: false,
    mode: "view", // 'view', 'edit', 'create'
    customer: null,
  });

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    customer: null,
    isBulkDelete: false,
    isLoading: false,
  });

  // Mock user data
  const currentUser = {
    name: "Admin User",
    email: "admin@crm.com",
  };

  // Pagination
  const totalItems = metaClient?.total;
  const totalPages = metaClient?.last_page;
  const startIndex = (currentPage - 1) * itemsPerPage;

  // Mock customer data
  const [customers] = useState([
    {
      id: "CUST001",
      name: "John Smith",
      email: "john.smith@techcorp.com",
      phone: "+1 (555) 123-4567",
      company: "TechCorp Solutions",
      status: "active",
      lastContact: "2025-01-15T10:30:00Z",
      address: "123 Business Ave",
      city: "New York",
      state: "NY",
      zipCode: "10001",
      notes:
        "Key decision maker for enterprise solutions. Prefers email communication.",
      recentOrders: [
        {
          id: "ORD001",
          date: "2025-01-10T00:00:00Z",
          total: 2500.0,
          status: "completed",
        },
        {
          id: "ORD002",
          date: "2024-12-15T00:00:00Z",
          total: 1800.0,
          status: "completed",
        },
      ],
    },
    {
      id: "CUST002",
      name: "Sarah Johnson",
      email: "sarah.j@innovatetech.com",
      phone: "+1 (555) 234-5678",
      company: "InnovateTech Inc",
      status: "active",
      lastContact: "2025-01-18T14:15:00Z",
      address: "456 Innovation Blvd",
      city: "San Francisco",
      state: "CA",
      zipCode: "94105",
      notes: "Interested in bulk orders. Schedule quarterly reviews.",
      recentOrders: [
        {
          id: "ORD003",
          date: "2025-01-12T00:00:00Z",
          total: 3200.0,
          status: "pending",
        },
      ],
    },
    {
      id: "CUST003",
      name: "Michael Chen",
      email: "m.chen@globalenterprises.com",
      phone: "+1 (555) 345-6789",
      company: "Global Enterprises",
      status: "pending",
      lastContact: "2025-01-10T09:45:00Z",
      address: "789 Corporate Dr",
      city: "Chicago",
      state: "IL",
      zipCode: "60601",
      notes: "New customer, pending contract approval.",
      recentOrders: [],
    },
    {
      id: "CUST004",
      name: "Emily Rodriguez",
      email: "emily.r@startupventures.com",
      phone: "+1 (555) 456-7890",
      company: "Startup Ventures LLC",
      status: "active",
      lastContact: "2025-01-20T16:20:00Z",
      address: "321 Startup Way",
      city: "Austin",
      state: "TX",
      zipCode: "73301",
      notes: "Fast-growing startup, potential for large orders.",
      recentOrders: [
        {
          id: "ORD004",
          date: "2025-01-18T00:00:00Z",
          total: 1500.0,
          status: "completed",
        },
      ],
    },
    {
      id: "CUST005",
      name: "David Wilson",
      email: "d.wilson@retailchain.com",
      phone: "+1 (555) 567-8901",
      company: "Retail Chain Corp",
      status: "inactive",
      lastContact: "2024-12-05T11:30:00Z",
      address: "654 Retail Plaza",
      city: "Miami",
      state: "FL",
      zipCode: "33101",
      notes: "Contract expired, needs renewal discussion.",
      recentOrders: [
        {
          id: "ORD005",
          date: "2024-11-20T00:00:00Z",
          total: 4200.0,
          status: "completed",
        },
      ],
    },
    {
      id: "CUST006",
      name: "Lisa Thompson",
      email: "lisa.t@manufacturingpro.com",
      phone: "+1 (555) 678-9012",
      company: "Manufacturing Pro",
      status: "active",
      lastContact: "2025-01-19T13:45:00Z",
      address: "987 Industrial Rd",
      city: "Detroit",
      state: "MI",
      zipCode: "48201",
      notes: "Long-term partner, excellent payment history.",
      recentOrders: [
        {
          id: "ORD006",
          date: "2025-01-16T00:00:00Z",
          total: 5800.0,
          status: "completed",
        },
        {
          id: "ORD007",
          date: "2025-01-05T00:00:00Z",
          total: 3400.0,
          status: "completed",
        },
      ],
    },
    {
      id: "CUST007",
      name: "Robert Garcia",
      email: "r.garcia@consultingfirm.com",
      phone: "+1 (555) 789-0123",
      company: "Elite Consulting Firm",
      status: "blocked",
      lastContact: "2024-11-30T10:15:00Z",
      address: "147 Professional Ave",
      city: "Denver",
      state: "CO",
      zipCode: "80202",
      notes: "Payment issues, account blocked pending resolution.",
      recentOrders: [],
    },
    {
      id: "CUST008",
      name: "Amanda Foster",
      email: "amanda.f@healthcaresys.com",
      phone: "+1 (555) 890-1234",
      company: "Healthcare Systems",
      status: "active",
      lastContact: "2025-01-21T08:30:00Z",
      address: "258 Medical Center Dr",
      city: "Phoenix",
      state: "AZ",
      zipCode: "85001",
      notes: "Healthcare sector client, requires compliance documentation.",
      recentOrders: [
        {
          id: "ORD008",
          date: "2025-01-14T00:00:00Z",
          total: 2800.0,
          status: "pending",
        },
      ],
    },
  ]);

  // Calculate customer statistics
  const customerStats = {
    total: customers.length,
    active: customers.filter((c) => c.status === "active").length,
    inactive: customers.filter((c) => c.status === "inactive").length,
    pending: customers.filter((c) => c.status === "pending").length,
    blocked: customers.filter((c) => c.status === "blocked").length,
    newThisMonth: customers.filter((c) => {
      const lastContact = new Date(c.lastContact);
      const thisMonth = new Date();
      return (
        lastContact.getMonth() === thisMonth.getMonth() &&
        lastContact.getFullYear() === thisMonth.getFullYear()
      );
    }).length,
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    dispatch(getClientsService({ page: page })).then((response) => {
      /* if (response.payload.status == 200) {
        s(response.payload?.data?.data);
      }*/
    });
  };

  const handleSelectCustomer = (customerId, isSelected) => {
    setSelectedCustomers((prev) =>
      isSelected
        ? [...prev, customerId]
        : prev.filter((id) => id !== customerId)
    );
  };

  const handleSelectAll = (isSelected) => {
    setSelectedCustomers(isSelected ? customers.map((c) => c.id) : []);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: "",
      location: "",
      dateFrom: "",
      dateTo: "",
    });
  };

  // Customer CRUD operations
  const handleAddCustomer = () => {
    setCustomerModal({
      isOpen: true,
      mode: "create",
      customer: null,
    });
  };

  const handleViewCustomer = (customer) => {
    setCustomerInfoModal({
      isOpenInfo: true,
      mode: "view",
      customer,
    });
  };

  const handleEditCustomer = (customer) => {
    setCustomerModal({
      isOpen: true,
      mode: "edit",
      customer,
    });
  };

  const handleDeleteCustomer = (customer) => {
    setDeleteModal({
      isOpen: true,
      customer,
      isBulkDelete: false,
      isLoading: false,
    });
  };

  const handleBulkAction = (action, customerIds) => {
    switch (action) {
      case "delete":
        setDeleteModal({
          isOpen: true,
          customer: null,
          isBulkDelete: true,
          isLoading: false,
        });
        break;
      case "export":
        handleExport(customerIds);
        break;
      case "activate":
        console.log("Activating customers:", customerIds);
        setSelectedCustomers([]);
        break;
      case "deactivate":
        console.log("Deactivating customers:", customerIds);
        setSelectedCustomers([]);
        break;
      default:
        break;
    }
  };

  const handleExport = (customerIds = null) => {
    const customersToExport = customerIds
      ? customers.filter((c) => customerIds.includes(c.id))
      : customers;

    console.log("Exporting customers:", customersToExport);
    // Implement export logic here
  };

  const handleSaveCustomer = async (customerData) => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    if (customerModal.mode === "create") {
      console.log("Creating new customer:", customerData);
    } else {
      console.log("Updating customer:", customerData);
    }

    setCustomerModal({ isOpen: false, mode: "view", customer: null });
  };

  const handleConfirmDelete = async () => {
    setDeleteModal((prev) => ({ ...prev, isLoading: true }));

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));

    if (deleteModal.isBulkDelete) {
      console.log("Deleting customers:", selectedCustomers);
      setSelectedCustomers([]);
    } else {
      console.log("Deleting customer:", deleteModal.customer);
    }

    setDeleteModal({
      isOpen: false,
      customer: null,
      isBulkDelete: false,
      isLoading: false,
    });
  };

  const handleCloseModal = () => {
    setCustomerModal({ isOpen: false, mode: "view", customer: null });
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      customer: null,
      isBulkDelete: false,
      isLoading: false,
    });
  };

  useEffect(() => {
    dispatch(getClientsService({ page: 1 }));
  }, [dispatch]);

  return (
    <div className="min-h-screen bg-surface">
      {/* Navigation Header 
      <NavigationHeader
        user={currentUser}
        onMenuToggle={handleSidebarToggle}
        sidebarCollapsed={sidebarCollapsed}
      />

   
      <MainNavigation
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />*/}

      {/* Breadcrumb Navigation 
        <BreadcrumbNavigation />

  
        <CustomerToolbar
          selectedCustomers={selectedCustomers}
          onAddCustomer={handleAddCustomer}
          onBulkAction={handleBulkAction}
          onExport={() => handleExport()}
          totalCustomers={customers.length}
        />*/}

      {/* Main Content Grid */}
      <div className="p-6">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          <div className="xl:col-span-12">
            <CustomerFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              customerStats={customerStats}
            />
          </div>

          {/* Left Section - Customer Table */}
          <div className="xl:col-span-9">
            <CustomerTable
              customers={clientsList}
              onEdit={handleEditCustomer}
              onDelete={handleDeleteCustomer}
              onView={handleViewCustomer}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              sortConfig={sortConfig}
              onSort={handleSort}
              selectedCustomers={selectedCustomers}
              onSelectCustomer={handleSelectCustomer}
              onSelectAll={handleSelectAll}
            />

            {/* Pagination */}

            <CustomerPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </div>

          <div className="xl:col-span-3">
            <CustomerDetails
              isOpen={customerInfoModal.isOpenInfo}
              onClose={handleCloseModal}
              customer={customerInfoModal.customer}
            />
          </div>
        </div>
      </div>

      {/* Customer Modal */}
      <CustomerModal
        isOpen={customerModal.isOpen}
        onClose={handleCloseModal}
        customer={customerModal.customer}
        mode={customerModal.mode}
        onSave={handleSaveCustomer}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        customer={deleteModal.customer}
        isLoading={deleteModal.isLoading}
        isBulkDelete={deleteModal.isBulkDelete}
        selectedCount={selectedCustomers.length}
      />
    </div>
  );
};

export default CustomerManagement;
